import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, MapPin, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany, useSalesOutlets } from './api';
import { scoreColor, CATEGORY_LABEL, formatDistance } from './lib';
import type { CompanyCategory } from './types';

const COMPONENT_MAX: Record<string, number> = {
  distance: 30, headcount: 25, warm: 25, category: 15, zoho: 8,
};
const COMPONENT_LABEL: Record<string, string> = {
  distance: 'Proximity to outlet',
  headcount: 'Estimated headcount',
  warm: 'Warm order signal',
  category: 'Category fit',
  zoho: 'Already in CRM (penalty)',
};
const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin', hr: 'HR', facilities: 'Facilities', office_manager: 'Office Manager',
  people_ops: 'People Ops', procurement: 'Procurement', founder: 'Founder', other: 'Contact',
};

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useCompany(id);
  const outletsQ = useSalesOutlets();
  const outletName = useMemo(
    () => Object.fromEntries((outletsQ.data ?? []).map((o) => [o.outlet_id, o.name])),
    [outletsQ.data],
  );

  if (q.isLoading) {
    return <div className="space-y-3 p-6"><Skeleton className="h-8 w-64" /><Skeleton className="h-48 w-full" /></div>;
  }
  if (q.error || !q.data) {
    return <div className="p-6 text-sm text-muted-foreground">Company not found.</div>;
  }

  const { company, locations, contacts, distances, signals } = q.data;
  const comps = company.score_components;
  const nearest = [...distances].sort((a, b) => Number(a.distance_m) - Number(b.distance_m))[0];
  const totalWarm = locations.reduce((s, l) => s + (l.orders_last_90d ?? 0), 0);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <Link to="/research" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to research
      </Link>

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{company.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">{CATEGORY_LABEL[company.category as CompanyCategory]}</Badge>
            {company.primary_city && <span>{company.primary_city}</span>}
            {nearest && <span>· {formatDistance(Number(nearest.distance_m))} from {outletName[nearest.outlet_id] ?? 'nearest outlet'}</span>}
            {company.exists_in_zoho && <Badge variant="outline">In Zoho CRM</Badge>}
            {company.website && (
              <a href={company.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground">
                <ExternalLink className="h-3 w-3" /> Website
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center rounded-xl px-4 py-2 text-white" style={{ backgroundColor: scoreColor(company.score) }}>
          <span className="font-mono text-3xl font-bold leading-none">{company.score ?? '—'}</span>
          <span className="text-2xs uppercase tracking-wide opacity-90">score</span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* score breakdown */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Score breakdown</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {comps ? (
              Object.keys(COMPONENT_MAX).map((k) => {
                const val = Number((comps as unknown as Record<string, number>)[k] ?? 0);
                const max = COMPONENT_MAX[k] ?? 1;
                const pct = Math.max(0, Math.min(100, (Math.abs(val) / max) * 100));
                const negative = val < 0;
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{COMPONENT_LABEL[k]}</span>
                      <span className="font-mono font-medium">{val > 0 ? '+' : ''}{val} / {negative ? `-${max}` : max}</span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-muted">
                      <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: negative ? '#B15599' : '#51BFAA' }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">Not scored yet.</p>
            )}
            {comps?.inputs && (
              <div className="grid grid-cols-2 gap-2 pt-2 text-xs text-muted-foreground sm:grid-cols-4">
                <Metric label="Nearest" value={formatDistance(comps.inputs.nearest_distance_m)} />
                <Metric label="Headcount" value={comps.inputs.est_headcount != null ? String(comps.inputs.est_headcount) : 'unknown'} />
                <Metric label="Warm 90d" value={String(comps.inputs.orders_last_90d)} />
                <Metric label="In CRM" value={comps.inputs.exists_in_zoho ? 'yes' : 'no'} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* contacts */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Contacts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No contacts yet. Enrich this company to add decision-makers.</p>
            ) : contacts.map((c) => (
              <div key={c.id} className="rounded-md border border-border/60 p-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{c.full_name ?? '—'}</span>
                  <Badge variant="outline" className="font-normal">{ROLE_LABEL[c.role_category] ?? c.role_category}</Badge>
                </div>
                {c.title && <div className="text-xs text-muted-foreground">{c.title}</div>}
                <div className="mt-1 space-y-0.5 text-xs">
                  {c.email && <div>{c.email}</div>}
                  {c.phone && <div>{c.phone}</div>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* locations + order history */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" /> Locations & order history</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {totalWarm > 0
                ? `${totalWarm} matched delivery orders (last 90d window) near this company.`
                : 'No matched delivery orders in the warm window yet.'}
            </p>
            {locations.map((l) => (
              <div key={l.id} className="rounded-md border border-border/60 p-2 text-sm">
                <div className="font-medium">{l.building_name ?? l.label ?? 'Location'}</div>
                {l.address && <div className="text-xs text-muted-foreground">{l.address}</div>}
                <div className="mt-1 flex gap-3 text-xs">
                  <span>{formatDistance(l.nearest_distance_m)} to {l.nearest_outlet_id ? outletName[l.nearest_outlet_id] ?? 'outlet' : 'outlet'}</span>
                  {l.orders_last_90d > 0 && <span className="font-semibold text-[#51BFAA]">{l.orders_last_90d} warm orders</span>}
                </div>
              </div>
            ))}
            {locations.length === 0 && <p className="text-sm text-muted-foreground">No locations.</p>}
          </CardContent>
        </Card>

        {/* activity timeline */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Activity timeline</CardTitle></CardHeader>
          <CardContent>
            {signals.length === 0 ? (
              <p className="text-sm text-muted-foreground">No signals recorded.</p>
            ) : (
              <ol className="space-y-2">
                {signals.map((s) => (
                  <li key={s.id} className="flex items-start gap-2 text-sm">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#252D63]" />
                    <div>
                      <span className="font-medium">{s.signal_type}</span>
                      {s.score_contribution != null && (
                        <span className="ml-1 font-mono text-xs text-muted-foreground">
                          {Number(s.score_contribution) > 0 ? '+' : ''}{s.score_contribution} pts
                        </span>
                      )}
                      <div className="text-2xs text-muted-foreground">{new Date(s.observed_at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>

      {company.notes && (
        <Card><CardContent className="p-3 text-sm"><span className="text-muted-foreground">Notes: </span>{company.notes}</CardContent></Card>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/60 px-2 py-1">
      <div className="text-2xs uppercase tracking-wide">{label}</div>
      <div className="font-mono text-sm text-foreground">{value}</div>
    </div>
  );
}
